import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, ButtonToolbar, FlexboxGrid, Form, SelectPicker } from "rsuite";

import "./CompanyFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { get } from "lodash/fp";
import { CompanyListParams } from "./CompanyDataTypes";
import { statusObjArr } from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
}

export default function CompanyFilters({
  onSubmit,
  onClear,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    code: "",
    type: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    mobile: "",
    contact_person: "",
    contact_person_email: "",
    contact_person_phone: "",
    prime_comp_admin: "",
    prime_comp_admin_email: "",
    prime_comp_admin_phone: "",
    status: "",
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] =
    useState<CompanyListParams>(initSearchFormData);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [areaList, setAreaList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [companyTypeList, setCompanyTypeList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const countryList = useSelector((state: any) => state.country.data);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  useEffect(() => {
    getCompanyTypes({});
  }, []);

  async function getCompanyTypes(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getCompanyTypes(params);
      setCompanyTypeList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function handleCountryChange(countryId: number) {
    try {
      const stateRes = await HelperServices.getAllValidStates({
        country: countryId,
      });
      setStateList(get("data.data", stateRes));
      resetSelectionFor("state");
      handleFormChanges({ path: "country", value: countryId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleStateChange(stateId: number) {
    try {
      const res = await HelperServices.getAllValidCities({ state: stateId });
      setCityList(res.data.data);
      resetSelectionFor("city");
      handleFormChanges({ path: "state", value: stateId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleCityChange(cityId: number) {
    try {
      const res = await HelperServices.getAllValidAreas({ city: cityId });
      setAreaList(res.data.data);
      resetSelectionFor("area");
      handleFormChanges({ path: "city", value: cityId });
    } catch (error) {
      console.log(error);
    }
  }

  async function handleAreaChange(areaId: number) {
    try {
      const res = await HelperServices.getAllValidBuildings({ area: areaId });
      setBuildingList(res.data.data);
      resetSelectionFor("building");
      handleFormChanges({ path: "area", value: areaId });
    } catch (error) {
      console.log(error);
    }
  }

  function clearSearch() {
    resetSelectionFor("country");
    onClear();
    setFormData(initSearchFormData);
  }

  function resetSelectionFor(resetFor: string) {
    let resetData: Object = {};
    switch (resetFor) {
      case "country":
        resetData = {
          country: "",
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setStateList([]);
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "state":
        resetData = {
          state: "",
          city: "",
          area: "",
          building: "",
        };
        setCityList([]);
        setAreaList([]);
        setBuildingList([]);
        break;
      case "city":
        resetData = {
          city: "",
          area: "",
          building: "",
        };
        setAreaList([]);
        setBuildingList([]);
        break;
      case "area":
        resetData = {
          area: "",
          building: "",
        };
        setBuildingList([]);
        break;
      case "building":
        resetData = {
          building: "",
        };
        break;
    }
    let newFormData = {
      ...formData,
      ...resetData,
    };
    setFormData(newFormData);
  }

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar className="mb-hide">
          <Button onClick={handleSearchToggle}>{t("ADVANCE_SEARCH")}</Button>
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="content pb-0 mb-3">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            layout="inline"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="name">
                  <Form.ControlLabel>
                    {t("COMPANY")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="name" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="code">
                  <Form.ControlLabel>
                    {t("COMPANY")} {t("CODE")}
                  </Form.ControlLabel>
                  <Form.Control name="code" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="type">
                  <Form.ControlLabel>
                    {t("COMPANY")} {t("TYPE")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={companyTypeList}
                    valueKey="id"
                    labelKey="type_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "type", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "type", value: "" });
                    }}
                    name="type"
                    value={formData?.type}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="mobile">
                  <Form.ControlLabel>{t("MOBILE")}</Form.ControlLabel>
                  <Form.Control name="mobile" type="number" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="pincode">
                  <Form.ControlLabel>{t("PINCODE")}</Form.ControlLabel>
                  <Form.Control name="pincode" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="contact_person">
                  <Form.ControlLabel>
                    {t("CONTACT_PERSON")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="contact_person" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="contact_person_phone">
                  <Form.ControlLabel>
                    {t("CONTACT_PERSON")} {t("PHONE")}
                  </Form.ControlLabel>
                  <Form.Control name="contact_person_phone" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="contact_person_email">
                  <Form.ControlLabel>
                    {t("CONTACT_PERSON")} {t("EMAIL")}
                  </Form.ControlLabel>
                  <Form.Control name="contact_person_email" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="prime_comp_admin">
                  <Form.ControlLabel>
                    {t("OWNER")} {t("NAME")}
                  </Form.ControlLabel>
                  <Form.Control name="prime_comp_admin" />
                </Form.Group>
              </FlexboxGrid.Item>

              <FlexboxGrid.Item>
                <Form.Group controlId="prime_comp_admin_phone">
                  <Form.ControlLabel>
                    {t("OWNER")} {t("PHONE")}
                  </Form.ControlLabel>
                  <Form.Control name="prime_comp_admin_phone" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="prime_comp_admin_email">
                  <Form.ControlLabel>
                    {t("OWNER")} {t("EMAIL")}
                  </Form.ControlLabel>
                  <Form.Control name="prime_comp_admin_email" />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="status">
                  <Form.ControlLabel>{t("STATUS")}</Form.ControlLabel>
                  <SelectPicker
                    data={statusObjArr}
                    valueKey="value"
                    labelKey="label"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "status", value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "status", value: "" });
                    }}
                    name="status"
                    value={formData?.status}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="country">
                  <Form.ControlLabel>{t("Country")}</Form.ControlLabel>
                  <SelectPicker
                    data={countryList}
                    valueKey="id"
                    labelKey="country_name"
                    onSelect={(value, item) => {
                      handleCountryChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("country");
                    }}
                    name="country"
                    value={formData?.country}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="state">
                  <Form.ControlLabel>{t("STATE")}</Form.ControlLabel>
                  <SelectPicker
                    data={stateList}
                    valueKey="id"
                    labelKey="state_name"
                    onSelect={(value, item) => {
                      handleStateChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("state");
                    }}
                    name="state"
                    value={formData?.state}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="city">
                  <Form.ControlLabel>{t("CITY")}</Form.ControlLabel>
                  <SelectPicker
                    data={cityList}
                    valueKey="id"
                    labelKey="city_name"
                    onSelect={(value, item) => {
                      handleCityChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("city");
                    }}
                    name="city"
                    value={formData?.city}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="area">
                  <Form.ControlLabel>{t("AREA")}</Form.ControlLabel>
                  <SelectPicker
                    data={areaList}
                    valueKey="id"
                    labelKey="area_name"
                    onSelect={(value, item) => {
                      handleAreaChange(value);
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("area");
                    }}
                    name="area"
                    value={formData?.area}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="building">
                  <Form.ControlLabel>{t("BUILDING")}</Form.ControlLabel>
                  <SelectPicker
                    data={buildingList}
                    valueKey="id"
                    labelKey="building_name"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "building", value });
                    }}
                    onClean={(e: any) => {
                      resetSelectionFor("building");
                    }}
                    name="building"
                    value={formData?.building}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>
            <div className="filter-btn">
              <Button onClick={() => clearSearch()}>{t("CLEAR_FILTER")}</Button>
              <Button onClick={() => onSubmit(formData)}>{t("SEARCH")}</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
